import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import PropertyCard from '@/components/PropertyCard';
import Seo from '@/components/Seo';
import { getProperties, getCitySummary, toggleSaveProperty, getSavedProperties } from '@/lib/api';
import { CITY_META, LOCALITIES, DEFAULT_CITY_IMAGE, slugify, deslugifyLocality } from '@/lib/cities';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import { MapPin, Building2, Home, KeyRound, TrendingUp } from 'lucide-react';

const PAGE_SIZE = 24;

const formatPrice = (price) => {
  if (!price) return '—';
  if (price >= 10000000) return `₹${(price / 10000000).toFixed(2)} Cr`;
  if (price >= 100000) return `₹${(price / 100000).toFixed(1)} Lac`;
  return `₹${price.toLocaleString('en-IN')}`;
};

const LocalityLandingPage = () => {
  const { citySlug, localitySlug } = useParams();
  const { user } = useAuth();
  const slug = (citySlug || '').toLowerCase();
  const meta = CITY_META[slug] || { name: slug ? slug.charAt(0).toUpperCase() + slug.slice(1) : 'India', state: 'India', image: DEFAULT_CITY_IMAGE };
  const city = meta.name;
  const locality = deslugifyLocality(slug, localitySlug)
    || (localitySlug || '').split('-').map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');

  const [summary, setSummary] = useState(null);
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [savedIds, setSavedIds] = useState(new Set());

  useEffect(() => {
    window.scrollTo(0, 0);
    const load = async () => {
      setLoading(true);
      try {
        const [sum, props] = await Promise.all([
          getCitySummary(city, locality),
          getProperties({ city, locality, skip: 0, limit: PAGE_SIZE }),
        ]);
        setSummary(sum);
        setProperties(props);
        setHasMore(props.length === PAGE_SIZE);
        if (user) {
          const saved = await getSavedProperties();
          setSavedIds(new Set(saved.map((p) => p.property_id)));
        }
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    };
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug, localitySlug, user]);

  const loadMore = async () => {
    setLoadingMore(true);
    try {
      const next = await getProperties({ city, locality, skip: properties.length, limit: PAGE_SIZE });
      setProperties((prev) => [...prev, ...next]);
      setHasMore(next.length === PAGE_SIZE);
    } catch (e) {
      toast.error('Failed to load more');
    } finally {
      setLoadingMore(false);
    }
  };

  const handleSave = async (propertyId) => {
    if (!user) { toast.error('Please sign in to save properties'); return; }
    try {
      const res = await toggleSaveProperty(propertyId);
      setSavedIds((prev) => {
        const s = new Set(prev);
        if (res.saved) s.add(propertyId); else s.delete(propertyId);
        return s;
      });
    } catch (e) { toast.error('Failed to save'); }
  };

  useEffect(() => {
    const id = 'locality-jsonld';
    document.getElementById(id)?.remove();
    const data = {
      '@context': 'https://schema.org',
      '@type': 'CollectionPage',
      name: `Property in ${locality}, ${city}`,
      description: `Buy, sell and rent verified property in ${locality}, ${city}.`,
      url: `https://bharat.estate/real-estate/${slug}/${localitySlug}`,
      about: { '@type': 'Place', name: `${locality}, ${city}`, address: { '@type': 'PostalAddress', addressLocality: locality, addressRegion: meta.state, addressCountry: 'IN' } },
    };
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.id = id;
    script.text = JSON.stringify(data);
    document.head.appendChild(script);
    return () => { document.getElementById(id)?.remove(); };
  }, [locality, city, slug, localitySlug, meta.state]);

  const total = summary?.total ?? properties.length;
  const otherLocalities = (LOCALITIES[slug] || []).filter((l) => slugify(l) !== localitySlug).slice(0, 8);

  return (
    <div className="min-h-screen bg-[#F9F9F7]" data-testid="locality-landing-page">
      <Seo
        title={`Property in ${locality}, ${city} — Buy & Rent Homes`}
        description={`Explore ${total}+ verified properties for sale and rent in ${locality}, ${city}. Apartments, villas, plots & commercial spaces with 0% brokerage on Bharat Estate.`}
        path={`/real-estate/${slug}/${localitySlug}`}
        image={meta.image}
      />

      {/* Hero */}
      <section className="relative" data-testid="locality-hero">
        <div className="absolute inset-0">
          <img src={meta.image} alt={`Property in ${locality}, ${city}`} className="w-full h-full object-cover"
            onError={(e) => { e.currentTarget.src = DEFAULT_CITY_IMAGE; }} />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0F172A] via-[#0F172A]/70 to-[#0F172A]/40" />
        </div>
        <div className="relative container-luxury py-20 md:py-28 pt-28 md:pt-36">
          <nav className="text-white/70 text-sm mb-5" aria-label="Breadcrumb">
            <Link to="/" className="hover:text-[#D4AF37]">Home</Link>
            <span className="mx-2">/</span>
            <Link to={`/real-estate/${slug}`} className="hover:text-[#D4AF37]">{city}</Link>
            <span className="mx-2">/</span>
            <span className="text-white">{locality}</span>
          </nav>
          <p className="caption text-[#D4AF37] mb-3 flex items-center gap-2">
            <MapPin className="h-4 w-4" /> {locality}, {city}
          </p>
          <h1 className="text-white text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-tight max-w-3xl">
            Property in {locality}, {city}
          </h1>
          <p className="text-white/80 text-base md:text-lg mt-4 max-w-2xl">
            Browse {total}+ verified homes in {locality} — apartments, villas, plots and commercial
            spaces, all with 0% brokerage.
          </p>

          <div className="flex flex-wrap gap-3 mt-8">
            <div className="bg-white/10 backdrop-blur-md border border-white/15 rounded-xl px-5 py-3" data-testid="locality-stat-total">
              <p className="text-[#D4AF37] text-2xl font-bold tabular-nums">{total}+</p>
              <p className="text-white/70 text-xs">Live listings</p>
            </div>
            {summary?.for_sale != null && (
              <div className="bg-white/10 backdrop-blur-md border border-white/15 rounded-xl px-5 py-3">
                <p className="text-white text-2xl font-bold tabular-nums">{summary.for_sale}</p>
                <p className="text-white/70 text-xs">For sale</p>
              </div>
            )}
            {summary?.for_rent != null && (
              <div className="bg-white/10 backdrop-blur-md border border-white/15 rounded-xl px-5 py-3">
                <p className="text-white text-2xl font-bold tabular-nums">{summary.for_rent}</p>
                <p className="text-white/70 text-xs">For rent</p>
              </div>
            )}
            {summary?.min_price && (
              <div className="bg-white/10 backdrop-blur-md border border-white/15 rounded-xl px-5 py-3">
                <p className="text-white text-2xl font-bold tabular-nums flex items-center gap-1">
                  <TrendingUp className="h-4 w-4 text-[#D4AF37]" /> {formatPrice(summary.min_price)}+
                </p>
                <p className="text-white/70 text-xs">Starting price</p>
              </div>
            )}
          </div>

          <div className="flex flex-wrap gap-3 mt-8">
            <Link to={`/properties?city=${encodeURIComponent(city)}&listing_type=buy`}>
              <Button className="bg-[#D4AF37] hover:bg-[#c19f30] text-[#0F172A] font-medium" data-testid="locality-cta-buy">
                <Home className="h-4 w-4 mr-2" /> Buy in {locality}
              </Button>
            </Link>
            <Link to={`/properties?city=${encodeURIComponent(city)}&listing_type=rent`}>
              <Button variant="outline" className="bg-white/10 border-white/30 text-white hover:bg-white/20" data-testid="locality-cta-rent">
                <KeyRound className="h-4 w-4 mr-2" /> Rent in {locality}
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Listings */}
      <section className="container-luxury py-14">
        <h2 className="text-2xl md:text-3xl font-bold text-[#0F172A] mb-8">Latest properties in {locality}</h2>
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <div key={i} className="bg-white rounded-sm animate-pulse">
                <div className="aspect-[4/3] bg-gray-200" />
                <div className="p-5 space-y-3"><div className="h-5 bg-gray-200 rounded w-3/4" /><div className="h-4 bg-gray-200 rounded w-1/2" /></div>
              </div>
            ))}
          </div>
        ) : properties.length > 0 ? (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6" data-testid="locality-listings-grid">
              {properties.map((p) => (
                <PropertyCard key={p.property_id} property={p} onSave={handleSave} isSaved={savedIds.has(p.property_id)} />
              ))}
            </div>
            {hasMore && (
              <div className="flex justify-center mt-10">
                <Button onClick={loadMore} disabled={loadingMore} variant="outline"
                  className="px-10 py-6 text-base border-[#0F172A]/20 hover:border-[#D4AF37] hover:text-[#D4AF37]"
                  data-testid="locality-load-more-btn">
                  {loadingMore ? 'Loading…' : `Load More in ${locality}`}
                </Button>
              </div>
            )}
          </>
        ) : (
          <div className="text-center py-20 bg-white rounded-sm">
            <Building2 className="h-16 w-16 mx-auto text-muted-foreground/30 mb-4" />
            <h3 className="text-xl font-medium text-[#0F172A] mb-2">No listings in {locality} yet</h3>
            <p className="text-muted-foreground mb-6">Explore more homes across {city}.</p>
            <Link to={`/real-estate/${slug}`}><Button variant="outline" data-testid="locality-browse-city">Browse all of {city}</Button></Link>
          </div>
        )}
      </section>

      {/* Nearby localities */}
      {otherLocalities.length > 0 && (
        <section className="bg-white border-t border-border/50">
          <div className="container-luxury py-12">
            <h2 className="text-xl font-bold text-[#0F172A] mb-5">Other areas in {city}</h2>
            <div className="flex flex-wrap gap-3">
              {otherLocalities.map((loc) => (
                <Link key={loc} to={`/real-estate/${slug}/${slugify(loc)}`}
                  className="px-4 py-2 rounded-full bg-[#F9F9F7] border border-border text-sm hover:border-[#D4AF37]"
                  data-testid={`nearby-locality-${slugify(loc)}`}>
                  {loc}
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}
    </div>
  );
};

export default LocalityLandingPage;
